import { CaseActivity } from "@/components/cases/case-activities";
import { CaseHistory } from "@/components/cases/case-history";
import CaseStages from "@/components/cases/case-stages";
import { ComplexCaseParty } from "@/components/cases/complex-case-party";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import FloatingAction from "@/components/core/actions/floating-action";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import DataGrid from "@/components/core/data-grid";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Input } from "@/components/core/forms/input.tsx";
import { useModal } from "@/components/core/modals/use-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import Tooltip from "@/components/core/tooltip";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { caseStages } from "@/constants/case-stages";
import { userPermissions } from "@/constants/user-permissions";
import { userRoles } from "@/constants/user-roles";
import { caseActivityQueryOptions, useCaseActivityMutation } from "@/features/case-evaluation/api/queries/acitivities-query";
import CaseEvidence from "@/features/case-evaluation/case-evidence";
import CasePaymentProof from "@/features/case-evaluation/case-payment-proof";
import CaseActivityModal from "@/features/case-evaluation/components/case-activity-modal";
import CaseEvaluationSettings from "@/features/case-evaluation/components/case-evaluation-settings";
import CaseInhibitModal from "@/features/case-evaluation/components/case-inhibit-modal";
import CaseStageUpdateModal from "@/features/case-evaluation/components/case-stage-update-modal";
import { caseMembersQueryOptions, casePartyMembersQueryOptions, evidenceCasePartyMembersQueryOptions } from "@/features/case-record/api/case-member-queries";
import { useCaseRecordOverviewMutation } from "@/features/case-record/api/queries/case-records-query";
import { caseRecordQueryOptions, useCaseRecordStatusMutation } from "@/features/case-record/api/queries/case-records-query";
import { caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query.ts";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import CaseEvidenceLayout from "@/features/new-case-ui/case-evidence-layout";
import { CaseBlock, CaseBlockBody, CaseBlockHead, CaseLayout } from "@/features/new-case-ui/case-layout";
import { HasPermission, HasRole, useAuth } from "@/hooks/use-auth";
import { toOrdinal } from "@/utils/format-data";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import { IconArrowRight, IconDeviceFloppy, IconPencil, IconSettings, IconX } from "@tabler/icons-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/cases_/$caseId")({
	validateSearch: z.object({
		status: z.string().optional(),
		search: z.string().optional(),
		per_page: z.number().optional(),
		stage: z.string().optional(),
		page: z.number().nullable().optional(),
		tabs: z.string().optional(),
		sort_by: z.enum(["latest", "oldest"]).optional(),
	}),
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		stage: search.stage || "PRELIMINARY_PROCEEDINGS",
		tabs: "",
	}),
	loader: async ({ context: { queryClient }, deps, params }) => {
		return queryClient.ensureQueryData(
			caseActivityQueryOptions({
				...deps,
				id: params.caseId,
			}),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const params = Route.useParams();
	const userQuery = useSuspenseQuery(caseRecordQueryOptions(params.caseId));

	const navigate = useNavigate();
	const data = userQuery.data;
	const caseStageQuery = useSuspenseQuery(
		caseStagesQueryOptions({
			case_classification: data.case_classification ?? "",
			status: "",
		}),
	);
	const caseStagesNew = caseStageQuery.data;
	const caseRecordStatusMutation = useCaseRecordStatusMutation(data?.id);

	const search = Route.useSearch();

	const [activeStage, setActiveStage] = useState("PRELIMINARY_PROCEEDINGS");
	const caseRecordOverviewMutation = useCaseRecordOverviewMutation();
	const caseOverviewColumns: ColumnDef<CaseRecord>[] = [
		{
			id: "case_category",
			label: "Case Category",
			cell: ({ data }) => <span>{data.case_category}</span>,
		},
		{
			id: "case_type",
			label: "Case Type",
			cell: ({ data }) => <span>{data.case_type}</span>,
		},
		{
			id: "case_title",
			label: "Case Title",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [title, setTitle] = useState(data.case_title);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseId,
						data: {
							column: "case_title",
							value: title,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="case_title"
									name="case_title"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.case_title}</span>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "nature_of_case",
			label: "Nature of Action",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [nature, setNature] = useState(data.nature_of_action);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseId,
						data: {
							column: "nature_of_action",
							value: nature,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={nature}
									onChange={(e) => setNature(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.nature_of_action || "N/A"}</span>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_filed",
			label: "Date Filed",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [filedAt, setFiledAt] = useState(data.filed_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseId,
						data: {
							column: "filed_at",
							value: filedAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={filedAt}
									type="datetime-local"
									onChange={(e) => setFiledAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.filed_at, "PP")}</span>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_raffled",
			label: "Date Raffled",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [raffledAt, setRaffledAt] = useState(data.raffled_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseId,
						data: {
							column: "raffled_at",
							value: raffledAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="nature_of_action"
									name="nature_of_action"
									value={raffledAt}
									type="datetime-local"
									onChange={(e) => setRaffledAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.raffled_at, "PP")}</span>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "date_received",
			label: "Date Received",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [receivedAt, setReceivedAt] = useState(data.raffled_at);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseId,
						data: {
							column: "received_at",
							value: receivedAt,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="received_at"
									name="received_at"
									value={receivedAt}
									type="datetime-local"
									onChange={(e) => setReceivedAt(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{formatDate(data.received_at, "PP")}</span>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "received_by",
			label: "Received By",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [receivedBy, setReceivedBy] = useState(data.received_by);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseId,
						data: {
							column: "received_by",
							value: receivedBy,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="received_by"
									name="received_by"
									value={receivedBy}
									onChange={(e) => setReceivedBy(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>{data.received_by || "N/A"}</span>
							</div>
						)}
					</>
				);
			},
		},
		{
			id: "claim_amount",
			label: "Claim Amount",
			cell: ({ data }) => {
				const [isEditing, setIsEditing] = useState(false);
				const [claimAmount, setClaimAmount] = useState(data.claim_amount);

				const handleSave = () => {
					caseRecordOverviewMutation.mutateAsync({
						caseRecordId: params.caseId,
						data: {
							column: "claim_amount",
							value: claimAmount,
						},
					});

					setIsEditing(false);
				};

				return (
					<>
						{isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									autoComplete="off"
									id="claim_amount"
									name="claim_amount"
									value={claim_amount}
									onChange={(e) => setClaimAmount(e.target.value)}
									className="border border-purple-400 focus:border-purple-500 focus:ring-purple-500 rounded-lg w-full px-3 py-2"
								/>

								<button type="button" onClick={handleSave} className="menu-button">
									<IconDeviceFloppy color="green" size={20} stroke={2} />
								</button>
								<button type="button" onClick={() => setIsEditing(false)} className="menu-button">
									<IconX color="red" size={20} stroke={2} />
								</button>
							</div>
						) : (
							<div className="flex items-center gap-2 group">
								<span>PHP {formatNumber(data.claim_amount) || "N/A"}</span>
							</div>
						)}
					</>
				);
			},
		},
	];

	const activityQuery = useQuery(
		caseActivityQueryOptions({
			stage: search.tabs === "case-history" ? "" : activeStage || data.stage || "",
			id: params.caseId,
			sort_by: search.sort_by,
		}),
	);

	const partyMembersQuery = useSuspenseQuery(
		casePartyMembersQueryOptions({
			case_record_id: data.id,
			role: "Defendant",
		}),
	);
	const partyMembers = partyMembersQuery.data;

	const multiplePartyMembersQuery = useSuspenseQuery(
		evidenceCasePartyMembersQueryOptions({
			case_record_id: data.id,
			role: "Defendant",
		}),
	);
	const multiplePartyMembers = multiplePartyMembersQuery.data;

	const activities = activityQuery.data?.data;
	const status = data.status;
	const modal = useModal();

	// get current stage
	const currentStageIndex = caseStagesNew.data.findIndex((stage) => stage.name === data.stage);
	const currentStage = currentStageIndex >= 0 ? caseStagesNew.data[currentStageIndex] : null;
	// get next stage
	const nextStage = currentStageIndex >= 0 ? caseStagesNew.data[currentStageIndex + 1] : null;

	const nextVisibleStage = caseStagesNew.data.slice(currentStageIndex + 1).find((stage) => stage.is_visible === true) || null;

	const openActivityModal = (stage = null, defaultActivity = null, moveTo = true, decision = false, isSummon = false, sheriffReport = false) => {
		modal.openModal({
			allowClose: false,
			title: "Add Activity",
			description: "New Activity",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: (
				<CaseActivityModal
					title={decision ? (stage ?? "") : (data.stage ?? "")}
					data={{
						stage: decision ? (stage ?? "") : (data.stage ?? ""),
						activity: defaultActivity || "",
					}}
					isSummon={isSummon}
					caseRecord={data}
					decision={decision}
					moveTo={moveTo}
					isSheriffReport={sheriffReport}
					nextStage={nextStage}
					nextVisibleStage={nextVisibleStage}
				/>
			),
		});
	};

	const openReceiveModal = () => {
		modal.openModal({
			title: "Receive Case",
			description: "You are receiving this case to proceed to the next step. Please confirm",
			type: "confirm",
			variant: "green",
			onConfirm: async () => {
				await caseRecordStatusMutation.mutateAsync({
					id: data.id,
					status: "received",
				});
				await navigate({
					to: `/cases/${data.id}`,
					search: (old) => ({
						...old,
						tabs: "case-flow",
					}),
				});
			},
		});
	};

	const openInhibitModal = () => {
		modal.openModal({
			allowClose: false,
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <CaseInhibitModal data={data} />,
		});
	};

	type Activity = {
		id: string;
		activity: string;
		stage: string;
	};

	const getGroupActivities = (activities: Activity[]): Record<string, Activity[]> => {
		let grouped = activities.reduce<Record<string, Activity[]>>((acc, activity) => {
			acc[activity.stage] = acc[activity.stage] || [];
			acc[activity.stage].push(activity);
			return acc;
		}, {});

		if (!grouped[data.stage ?? ""]) {
			grouped[data.stage ?? ""] = [];
		}

		grouped = Object.fromEntries(Object.entries(grouped).filter(([key]) => key !== "null"));

		return grouped;
	};

	const [tabValue, setTabValue] = useState(caseStages.PRELIMINARY_PROCEEDINGS);
	const [mainTabValue, setMainTabValue] = useState("case-details");

	useEffect(() => {
		if (search.stage) {
			setTabValue(search.stage);
		}
		if (search.tabs) {
			if (search.tabs === "case-history") {
				setTabValue("");
			}
			setMainTabValue(search.tabs);
		}
	}, [search.stage, search.tabs]);

	return (
		<Content pageName="Case Information">
			{/*<Header title="Case Information" />*/}

			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center w-full max-w-full justify-between">
					<div className="inline-flex flex-col w-auto">
						<div className="inline-flex flex-row items-center gap-2">
							<span className="font-bold text-xl text-foreground-dark">Case No. {data.case_number}</span>
							{data.stage && (
								<Badge className="uppercase" size="xs" color="green-light">
									{data.stage.toLowerCase().replace(/_/g, " ")} {data.stage === "JUDGMENT" && " / Case Closed"}
								</Badge>
							)}
							{data.archived_at && (
								<Tooltip content={formatDate(data.archived_at, "PP")} side="top">
									<div>
										<Badge className="uppercase cursor-pointer" size="xs" color="red-light">
											Archived
										</Badge>
									</div>
								</Tooltip>
							)}
							{data.case_classification && (
								<Badge className="uppercase" size="xs" color="green-light">
									{data.case_classification}
								</Badge>
							)}
							{data.allow_post_as && (
								<Badge className="uppercase" size="xs" color="green-light">
									Allow "Post As Admin"
								</Badge>
							)}
							{data.decision_at && (
								<Tooltip content="Appeal Deadline" side="top">
									<div>
										<Badge className="uppercase cursor-pointer" size="xs" color="yellow-light">
											{formatDate(data.decision_at, "PP")}
										</Badge>
									</div>
								</Tooltip>
							)}
						</div>
						<span className="text-base text-foreground/70">
							{data.case_title} - {data.branch}
						</span>
					</div>
				</div>
				<div className="flex flex-col w-full gap-4">
					<Tabs
						value={mainTabValue}
						defaultValue={search.tabs || "case-details"}
						onValueChange={async (value) => {
							await navigate({
								to: `/cases/${data.id}`,
								search: (old) => ({
									...old,
									tabs: value,
								}),
							});
						}}
					>
						<TabsList>
							<HasPermission permissions={[userPermissions.VIEW_CASE_DETAILS]}>
								<TabsTrigger value="case-details">Case Details</TabsTrigger>
							</HasPermission>
							<HasPermission permissions={[userPermissions.VIEW_EVIDENCE]}>
								<TabsTrigger value="case-evidence">Pieces of Evidence</TabsTrigger>
							</HasPermission>
							<HasPermission permissions={[userPermissions.VIEW_CASE_ACTIVITIES]}>
								<TabsTrigger value="case-flow" disabled={data.status === "branch_receiving"}>
									Case Activities
								</TabsTrigger>
							</HasPermission>
							<HasPermission permissions={[userPermissions.VIEW_CASE_HISTORY]}>
								<TabsTrigger value="case-history" disabled={data.status === "branch_receiving"}>
									Case History
								</TabsTrigger>
							</HasPermission>
						</TabsList>

						<HasPermission permissions={[userPermissions.VIEW_CASE_DETAILS]}>
							<TabsContent value="case-details">
								<CaseLayout>
									<CaseBlock showContent={true}>
										<CaseBlockHead>
											<div className="flex flex-col w-auto">
												<span className="text-base font-bold text-foreground-dark">Case Parties</span>
											</div>
										</CaseBlockHead>
										<CaseBlockBody className="border border-border/50">
											<ComplexCaseParty
												isReadOnly={true}
												plaintiffs={data.plaintiffs}
												defendants={data.case_defendants}
												caseData={data}
												parties={Object.entries(partyMembers)}
											/>
										</CaseBlockBody>
									</CaseBlock>
									<CaseBlock showContent={false}>
										<CaseBlockHead>
											<div className="flex flex-col w-auto">
												<span className="text-base font-bold text-foreground-dark">Case Overview</span>
											</div>
										</CaseBlockHead>
										<CaseBlockBody className="border border-border/50">
											<DataGrid data={data} columns={caseOverviewColumns} />
										</CaseBlockBody>
									</CaseBlock>
									<CaseBlock showContent={false}>
										<CaseBlockHead>
											<div className="flex flex-col w-auto">
												<span className="text-base font-bold text-foreground-dark">Proof of Payment</span>
											</div>
										</CaseBlockHead>
										<CaseBlockBody className="border border-border/50">
											<div className="flex flex-col w-full gap-2">
												<CasePaymentProof
													data={data}
													title="Upload Files"
													counts={0}
													allowAdd={data.status === "received"}
													allowRemove={true}
													allowPreview={false}
													allowPrint={false}
													allowDownload={false}
													isReadOnly={true}
												/>
											</div>
										</CaseBlockBody>
									</CaseBlock>
								</CaseLayout>
							</TabsContent>
						</HasPermission>

						<HasPermission permissions={[userPermissions.VIEW_EVIDENCE]}>
							<TabsContent value="case-evidence">
								<div className="flex flex-col w-full mt-6 gap-2">
									<div className="flex flex-col w-auto">
										<span className="text-base font-bold text-foreground-dark">Pieces of Evidence</span>
									</div>
									<div className="flex flex-col w-full rounded-md border border-border/50">
										<div className="flex flex-col w-full">
											<div className="flex flex-row w-full">
												<Tabs defaultValue="initial-dispute" variant="compact">
													<TabsList className="pt-4">
														<TabsTrigger value="initial-dispute">Primary Complaint</TabsTrigger>
														{Object.entries(multiplePartyMembers).map(([key, array]) => (
															<TabsTrigger key={`tabs_${key}`} value={key}>
																{toOrdinal(Number.parseInt(key) + 2)} Party Complaint
															</TabsTrigger>
														))}
													</TabsList>
													<TabsContent value="initial-dispute">
														<div className="flex flex-col w-full h-full">
															<CaseEvidence data={data} readonly={data.status === "branch_receiving"} depth="0" />
														</div>
													</TabsContent>

													{Object.entries(multiplePartyMembers).map(([key, array]) => (
														<TabsContent key={`content_${key}`} value={key}>
															<div className="flex flex-col w-full">
																<CaseEvidence data={data} readonly={data.status === "branch_receiving"} depth={key} members={array} />
															</div>
														</TabsContent>
													))}
												</Tabs>
											</div>
										</div>
									</div>
								</div>
							</TabsContent>
						</HasPermission>

						<HasPermission permissions={[userPermissions.VIEW_CASE_ACTIVITIES]}>
							<TabsContent value="case-flow">
								<CaseStages
									value={data.stage}
									// value={data.stage}
									stages={caseStagesNew.data
										.filter((c) => c.is_visible)
										.map((c) => ({
											label: c.label,
											value: c.name,
										}))}
									activities={(stage) => {
										setActiveStage(stage);
										return (
											activities && (
												<div className="flex flex-col w-full gap-4">
													<CaseActivity
														nextVisibleStage={nextVisibleStage}
														data={data}
														activities={activities}
														activeStatus={data.stage}
														currentTab={stage}
														nextStage={nextStage}
														isReadOnly={true}
														sortBy={search.sort_by || "latest"}
														onSortChange={(sortBy) => {
															navigate({
																search: (prev) => ({ ...prev, sort_by: sortBy }),
															});
														}}
													/>
												</div>
											)
										);
									}}
								/>
							</TabsContent>
						</HasPermission>

						<HasPermission permissions={[userPermissions.VIEW_CASE_HISTORY]}>
							<TabsContent value="case-history">
								{activities !== undefined && <CaseHistory groups={Object.entries(getGroupActivities(activities))} activities={activities} data={data} />}
							</TabsContent>
						</HasPermission>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}
